import { ChakraProvider } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';

import QueryContext from '~sf/api/QueryContext';
import ErrorBoundary from '~sf/components/ui/ErrorBoundary';
import translationMessages from '~sf/i18n';
import LoadingPage from '~sf/pages/LoadingPage';

import safebeatTheme from './theme';

const DEFAULT_LOCALE = 'en';

const nonceElement = document.querySelector('meta[name="csp-nonce"]');
const cspNonce =
  nonceElement !== null && nonceElement instanceof HTMLMetaElement ? nonceElement.content : '';

const styleCache = createCache({
  key: 'emotion-styles',
  nonce: cspNonce,
});

export type SetupContextProps = {
  children: React.ReactNode;
};

const SetupContext = ({ children }: SetupContextProps) => {
  const AppContent = (
    <CacheProvider value={styleCache}>
      <ChakraProvider resetCSS theme={safebeatTheme}>
        <HelmetProvider>
          <QueryContext>
            <Suspense fallback={<LoadingPage />}>
              <ErrorBoundary mode="page">
                {/* ReactQueryDevtools are automatically excluded from prod builds */}
                <ReactQueryDevtools initialIsOpen={false} />

                {children}
              </ErrorBoundary>
            </Suspense>
          </QueryContext>
        </HelmetProvider>
      </ChakraProvider>
    </CacheProvider>
  ) as IntlProvider['props']['children'];

  return (
    <IntlProvider
      messages={translationMessages[DEFAULT_LOCALE]}
      locale={DEFAULT_LOCALE}
      defaultLocale={DEFAULT_LOCALE}
    >
      {AppContent}
    </IntlProvider>
  );
};

export default SetupContext;
